// ISO/IEC 27001:2022 Annex A controls-mapping tab body — extracted
// from the original AdminComplianceISO27001 page so it can render
// inside the consolidated `/admin/compliance` shell alongside the
// SOC 2 evidence-engine tab.

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ClipboardList,
  Download,
  HelpCircle,
  Info,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import {
  useISO27001Controls,
  useISO27001Snapshot,
  buildSoACsv,
  SECTION_TITLES,
  STATUS_LABELS,
  type ISO27001Control,
  type ISO27001Section,
  type ISO27001Status,
  type ISO27001Probe,
  type ProbeTone,
} from '../../api/iso27001';

const STATUS_TONES: Record<ISO27001Status, 'success' | 'accent' | 'neutral'> = {
  implemented: 'success',
  configurable: 'accent',
  customer_side: 'neutral',
};

const PROBE_TONES: Record<
  ProbeTone,
  { tone: 'success' | 'warning' | 'danger' | 'neutral'; Icon: typeof CheckCircle }
> = {
  ok: { tone: 'success', Icon: CheckCircle },
  warn: { tone: 'warning', Icon: AlertTriangle },
  fail: { tone: 'danger', Icon: XCircle },
  info: { tone: 'neutral', Icon: Info },
};

function probeToneIconClassName(tone: 'success' | 'warning' | 'danger' | 'neutral') {
  if (tone === 'success') return 'text-success';
  if (tone === 'warning') return 'text-warning';
  if (tone === 'danger') return 'text-danger';
  return 'text-fg-muted';
}

const SECTIONS: ISO27001Section[] = ['A.5', 'A.6', 'A.7', 'A.8'];

const SELECT_CLASS =
  'px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface ProbeChipProps {
  readonly probe: ISO27001Probe | undefined;
}

function ProbeChip({ probe }: ProbeChipProps) {
  if (!probe) return null;
  const { tone, Icon } = PROBE_TONES[probe.tone];
  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge tone={tone}>
        <Icon className="h-3.5 w-3.5 mr-1" />
        {probe.headline}
      </Badge>
    </span>
  );
}

interface ControlRowProps {
  readonly control: ISO27001Control;
  readonly probe: ISO27001Probe | undefined;
}

function ControlRow({ control, probe }: ControlRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = probe?.details && Object.keys(probe.details).length > 0;
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-5 py-3 align-top whitespace-nowrap font-mono text-xs text-fg">
        {control.id}
      </td>
      <td className="px-5 py-3 align-top">
        <div className="font-medium text-fg">{control.title}</div>
        {control.feature && (
          <div className="text-xs text-fg-muted mt-1 max-w-2xl">{control.feature}</div>
        )}
        {control.evidence && (
          <div className="text-xs text-fg-subtle mt-1 font-mono">
            Evidence: {control.evidence}
          </div>
        )}
        {probe && (
          <div className="mt-2">
            <ProbeChip probe={probe} />
            {hasDetails && (
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="ml-2 text-xs text-accent hover:underline"
              >
                {expanded ? 'Hide details' : 'Show details'}
              </button>
            )}
          </div>
        )}
        {expanded && hasDetails && (
          <pre className="mt-2 text-xs bg-bg-hover/50 rounded-md p-2 overflow-x-auto font-mono text-fg-muted max-w-2xl whitespace-pre-wrap">
            {JSON.stringify(probe.details, null, 2)}
          </pre>
        )}
      </td>
      <td className="px-5 py-3 align-top whitespace-nowrap">
        <Badge tone={STATUS_TONES[control.status]}>
          {STATUS_LABELS[control.status]}
        </Badge>
      </td>
    </tr>
  );
}

export function AdminComplianceIso27001Tab() {
  const { data: catalogue } = useISO27001Controls();
  const { data: snapshot, mutate: refreshSnapshot, isValidating } = useISO27001Snapshot();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ISO27001Status | 'all'>('all');
  const [sectionFilter, setSectionFilter] = useState<ISO27001Section | 'all'>('all');

  const controls = catalogue?.controls ?? snapshot?.controls ?? [];
  const states = snapshot?.states ?? {};
  const counts = catalogue?.counts ?? snapshot?.counts;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return controls.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (sectionFilter !== 'all' && c.section !== sectionFilter) return false;
      if (q && !`${c.id} ${c.title} ${c.feature ?? ''} ${c.evidence ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [controls, search, statusFilter, sectionFilter]);

  const grouped = useMemo(() => {
    const out: Record<ISO27001Section, ISO27001Control[]> = {
      'A.5': [],
      'A.6': [],
      'A.7': [],
      'A.8': [],
    };
    for (const c of filtered) {
      out[c.section].push(c);
    }
    return out;
  }, [filtered]);

  const probedControls = useMemo(
    () => controls.filter((c) => c.snapshot_key && states[c.snapshot_key]),
    [controls, states],
  );

  return (
    <>
      <Card>
        <CardHeader
          title="Live snapshot"
          description={
            snapshot
              ? `Generated ${new Date(snapshot.generated_at).toLocaleString()} · refreshes every 30s.`
              : 'Loading live probes…'
          }
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => downloadCsv('orbitalreg-iso27001-soa.csv', buildSoACsv(controls))}
                disabled={controls.length === 0}
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export SoA (CSV)
              </Button>
              <Button variant="ghost" size="sm" onClick={() => refreshSnapshot()}>
                <RefreshCw className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          }
        />
        <CardBody>
          {counts && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge tone="success">
                {counts.implemented} Implemented
              </Badge>
              <Badge tone="accent">
                {counts.configurable} Configurable
              </Badge>
              <Badge tone="neutral">
                {counts.customer_side} Customer-side
              </Badge>
              <span className="text-xs text-fg-subtle ml-2">
                {controls.length} total · ISO/IEC 27001:2022 Annex A
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {probedControls.map((c) => {
              const probe = states[c.snapshot_key!];
              const { tone, Icon } = PROBE_TONES[probe.tone];
              return (
                <div
                  key={c.id}
                  className="rounded-md border border-border p-3 bg-bg/40 flex items-start gap-3"
                >
                  <Icon
                    className={`h-5 w-5 mt-0.5 shrink-0 ${probeToneIconClassName(tone)}`}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-fg-subtle">{c.id}</div>
                    <div className="text-sm font-medium text-fg truncate">{c.title}</div>
                    <div className="text-xs text-fg-muted mt-1">{probe.headline}</div>
                  </div>
                </div>
              );
            })}
            {probedControls.length === 0 && (
              <p className="text-sm text-fg-muted">
                Live probes are still loading.
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card className="mt-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-fg-muted" />
              All Annex A controls
            </span>
          }
          description="Filter by status or section, search across ID / title / feature / evidence. Rows with a live probe show a coloured chip alongside the static status."
        />
        <CardBody>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                type="search"
                placeholder="Search controls (e.g. backup, A.5.7, audit_events)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ISO27001Status | 'all')}
              className={SELECT_CLASS}
            >
              <option value="all">All statuses</option>
              <option value="implemented">Implemented</option>
              <option value="configurable">Configurable</option>
              <option value="customer_side">Customer-side</option>
            </select>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value as ISO27001Section | 'all')}
              className={SELECT_CLASS}
            >
              <option value="all">All sections</option>
              {SECTIONS.map((s) => (
                <option key={s} value={s}>
                  {s} — {SECTION_TITLES[s]}
                </option>
              ))}
            </select>
            <span className="text-sm text-fg-muted">
              {filtered.length} of {controls.length} controls
            </span>
          </div>

          <div className="overflow-x-auto">
            {SECTIONS.map((sec) => {
              const rows = grouped[sec];
              if (rows.length === 0) return null;
              return (
                <div key={sec} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-semibold text-fg mb-2 flex items-center gap-2">
                    <span className="font-mono">{sec}</span>
                    <span className="text-fg-muted font-normal">
                      — {SECTION_TITLES[sec]}
                    </span>
                    <span className="text-xs text-fg-subtle font-normal">
                      ({rows.length})
                    </span>
                  </h3>
                  <table className="w-full text-sm border border-border rounded-md overflow-hidden">
                    <thead className="bg-bg-hover/40">
                      <tr className="text-left">
                        <th className="px-5 py-2 font-medium text-fg-muted w-24">ID</th>
                        <th className="px-5 py-2 font-medium text-fg-muted">Control</th>
                        <th className="px-5 py-2 font-medium text-fg-muted w-32">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => (
                        <ControlRow
                          key={c.id}
                          control={c}
                          probe={c.snapshot_key ? states[c.snapshot_key] : undefined}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-5 py-8 text-center text-fg-muted text-sm">
                No controls match the current filters.
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card className="mt-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-fg-muted" />
              What this page does not do
            </span>
          }
          description="A few clarifications worth making explicit when handing the page to an auditor."
        />
        <CardBody>
          <ul className="text-sm text-fg-muted space-y-2 list-disc list-inside">
            <li>
              It does <strong>not</strong> assert that this deployment is automatically
              ISO 27001-certified by virtue of running OrbitalReg. Certification is granted
              against the customer's ISMS by an accredited body; the registry is one technical
              control source among many.
            </li>
            <li>
              It does <strong>not</strong> replace the auditor's independent verification.
              Use the page to short-cut the <em>what does the product do?</em> conversation,
              then have the auditor reproduce the assertions from the cited APIs and admin
              pages.
            </li>
            <li>
              It does <strong>not</strong> map the management-system clauses (4–10) of
              ISO 27001:2022. Those describe <em>how</em> the ISMS is run — leadership
              commitment, risk treatment, performance evaluation — and sit entirely with
              the customer.
            </li>
          </ul>
        </CardBody>
      </Card>
    </>
  );
}
